import { Can } from '@/components/can/Can';
import { Form } from '@/components/forms';
import Input from '@/components/forms/Input';
import MultiSelect from '@/components/forms/MultiSelect';
import Select from '@/components/forms/Select';
import { Button } from '@/design-system/button';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { FileObject, uploadFileToS3WithPresignedUrl } from '@/lib/uploadToAWS';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { SNS } from '../../Sns';
import { closingDateOptions } from '../../store-management.constant';
import { StoreInfo } from '../../StoreInfo';
import { useGetStoreDetails } from '../hooks/useGetStoreDetails';
import useUpdateStoreInfo from '../hooks/useUpdateStoreInfo';
import { useUpdateStoreFunction } from '../utils/updateStoreFunctions';

export const UpdateStoreForm = () => {
  const clubId = useParams({ from: '/(app)/store-information/basic-stores/$manage-store/' });
  const { getBasicStoreDetails, isSuccess, isLoading } = useGetStoreDetails(clubId['manage-store']);

  const storeKey =
    getBasicStoreDetails?.id ??
    getBasicStoreDetails?.clubId ??
    getBasicStoreDetails?.storeId ??
    getBasicStoreDetails?.clubCode ??
    null;
  const { mutateAsync } = useUpdateStoreInfo();
  const {
    methods,
    options,
    lastEntryOptions,
    prefectureOptions,
    holidaysOptions,
    otherHolidaysOptions,
    profileImage,
    coverImage,
    setProfileImage,
    setCoverImage,
  } = useUpdateStoreFunction();

  const optionsReady =
    prefectureOptions.length && options.length && holidaysOptions.length && otherHolidaysOptions.length;

  useEffect(() => {
    if (!getBasicStoreDetails || !storeKey || !isSuccess) return;
    if (!optionsReady) return;

    methods.reset({
      storeName: getBasicStoreDetails.storeName || '',
      storeNameFurigana: getBasicStoreDetails.storeNameFurigana || '',
      groupName: getBasicStoreDetails.groupName || '',
      landlineNumber: getBasicStoreDetails.landlineNumber || '',
      description: getBasicStoreDetails.description || '',
      postalCode: getBasicStoreDetails.postalCode || '',
      prefecture: getBasicStoreDetails.prefecture || '',
      city: getBasicStoreDetails.city || '',
      street: getBasicStoreDetails.street || '',
      buildingNo: getBasicStoreDetails.buildingNo || '',
      workingTimeFrom: getBasicStoreDetails.workingTimeFrom || '',
      workingTimeTo: getBasicStoreDetails.workingTimeTo || '',
      lastEntryTime: getBasicStoreDetails.lastEntryTime || '',
      lastOrderTime: getBasicStoreDetails.lastOrderTime || '',
      holidays: getBasicStoreDetails.holidays || '',
      otherHolidays: getBasicStoreDetails.otherHolidays || [],
      tax: getBasicStoreDetails.tax || '',
      closingDate: getBasicStoreDetails.closingDate || '',
      instagram: getBasicStoreDetails.instagram || '',
      tiktok: getBasicStoreDetails.tiktok || '',
      line: getBasicStoreDetails.line || '',
      twitter: getBasicStoreDetails.twitter || '',
      website: getBasicStoreDetails.website || '',
      googleMap: getBasicStoreDetails.googleMap || '',
    });
    setProfileImage(getBasicStoreDetails?.profileImage?.path || '');
    setCoverImage(getBasicStoreDetails?.coverImage?.path || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeKey,
    getBasicStoreDetails,
    isSuccess,
    optionsReady,
    options,
    prefectureOptions,
    holidaysOptions,
    otherHolidaysOptions,
  ]);

  // lastEntryOptions field still shows required error even if an option is selected without this useEffect
  useEffect(() => {
    const subscription = methods.watch((_value, { name }) => {
      if (name === 'workingTimeTo' && lastEntryOptions.length > 0) {
        // Auto-select the last option
        const lastOption = lastEntryOptions[lastEntryOptions.length - 1];
        methods.setValue('lastEntryTime', lastOption.value, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, lastEntryOptions]);

  const { t } = useDynamicLocale();
  return (
    <Form
      form={methods}
      onSubmit={async () => {
        const formData = methods.getValues();

        const existingProfile = getBasicStoreDetails?.profileImage?.path ?? '';
        const existingCover = getBasicStoreDetails?.coverImage?.path ?? '';

        let profileImagePayload = existingProfile;
        let coverImagePayload = existingCover;

        // removed explicitly
        if (formData.profileImage === '') profileImagePayload = '';
        if (formData.coverImage === '') coverImagePayload = '';
        if (!profileImagePayload) {
          toast.error(t('common.form.validation.profileUpload'));
          return;
        }
        if (!coverImagePayload) {
          toast.error(t('common.form.validation.coverUpload'));
          return;
        }
        // new upload
        if (typeof formData.profileImage === 'object' && formData.profileImage) {
          const profileImageFile = formData.profileImage.valueOf();
          const uploaded = await uploadFileToS3WithPresignedUrl(profileImageFile as FileObject);
          if (uploaded) profileImagePayload = uploaded;
        }

        if (typeof formData.coverImage === 'object' && formData.coverImage) {
          const coverImageFile = formData.coverImage.valueOf();
          const uploaded = await uploadFileToS3WithPresignedUrl(coverImageFile as FileObject);
          if (uploaded) coverImagePayload = uploaded;
        }

        const payload = {
          clubId: clubId['manage-store'] || '',
          profileImage: profileImagePayload,
          coverImage: coverImagePayload,
          storeName: formData.storeName || '',
          storeNameFurigana: formData.storeNameFurigana || '',
          groupName: formData.groupName || '',
          landlineNumber: formData.landlineNumber || '',
          description: formData.description || '',
          postalCode: formData.postalCode || '',
          prefecture: formData.prefecture || '',
          city: formData.city || '',
          street: formData.street || '',
          buildingNo: formData.buildingNo || '',
          workingTimeFrom: formData.workingTimeFrom || '',
          workingTimeTo: formData.workingTimeTo || '',
          lastEntryTime: formData.lastEntryTime || '',
          lastOrderTime: formData.lastOrderTime || '',
          holidays: formData.holidays || '',
          tax: formData.tax || '',
          closingDate: formData.closingDate || '',
          otherHolidays: formData.otherHolidays || [],
          instagram: formData.instagram || '',
          tiktok: formData.tiktok || '',
          line: formData.line || '',
          twitter: formData.twitter || '',
          website: formData.website || '',
          googleMap: formData.googleMap || '',
        };

        await mutateAsync(payload);
      }}
      className="relative"
    >
      <fieldset disabled={!storeKey || isLoading}>
        <div className="relative">
          <StoreInfo
            profileImagePath={profileImage}
            setProfileImage={(img) => {
              setProfileImage(img);
              methods.setValue('profileImage', img ? undefined : '');
            }}
            coverImagePath={coverImage}
            setCoverImage={(img) => {
              setCoverImage(img);
              methods.setValue('coverImage', img ? undefined : '');
            }}
          />

          <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20 mt-4">
            <div className="w-[200px] xl:w-[245px] shrink-0">
              <p className="text-base font-semibold pl-12">
                <Translation id="storeManagement.basicStores.manageStoreInfo.clubAddress" />
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <Controller
                name="postalCode"
                control={methods.control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    label="storeManagement.basicStores.manageStoreInfo.clubAddress.postalCode"
                    placeholder="100-0101"
                    inputMode="numeric"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      let value = e.target.value;

                      // keep digits only
                      const digits = value.replace(/\D/g, '');

                      // max 7 digits
                      if (digits.length > 7) return;

                      // format as XXX-XXXX
                      if (digits.length <= 3) {
                        value = digits;
                      } else {
                        value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
                      }

                      field.onChange(value);
                    }}
                  />
                )}
              />

              <Select
                options={prefectureOptions}
                placeholder="Select Prefecture"
                label="storeManagement.basicStores.manageStoreInfo.clubAddress.prefecture"
                selected={getBasicStoreDetails?.prefecture}
                {...methods.register('prefecture')}
                disabled
              />

              <Input
                label="storeManagement.basicStores.manageStoreInfo.clubAddress.city"
                maxLength={50}
                {...methods.register('city')}
                placeholder={t('manageStoreInformation.form.description.enterCity')}
              />
              <Input
                label="storeManagement.basicStores.manageStoreInfo.clubAddress.street"
                maxLength={50}
                {...methods.register('street')}
                placeholder={t('manageStoreInformation.form.description.enterStreet')}
              />
              <Input
                label="storeManagement.basicStores.manageStoreInfo.clubAddress.buildingAndRoomNo"
                maxLength={50}
                {...methods.register('buildingNo')}
                placeholder={t('manageStoreInformation.form.description.enterBuildingNo')}
              />
            </div>
          </div>

          <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20 mt-4">
            <div className="w-[200px] xl:w-[245px] shrink-0">
              <p className="text-base font-semibold pl-12">
                <Translation id="storeManagement.basicStores.manageStoreInfo.clubInfo" />
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <Select
                options={options}
                placeholder="HH:MM"
                label="storeManagement.basicStores.manageStoreInfo.clubInfo.workingHoursFrom"
                selected={getBasicStoreDetails?.workingTimeFrom}
                {...methods.register('workingTimeFrom')}
              />
              <Select
                options={options}
                placeholder="HH:MM"
                label="storeManagement.basicStores.manageStoreInfo.clubInfo.workingHoursTo"
                selected={getBasicStoreDetails?.workingTimeTo}
                {...methods.register('workingTimeTo')}
              />
              <Select
                options={lastEntryOptions}
                placeholder="HH:MM"
                label="storeManagement.basicStores.manageStoreInfo.clubInfo.lastEntryTime"
                selected={getBasicStoreDetails?.lastEntryTime}
                {...methods.register('lastEntryTime')}
              />
              <Select
                options={holidaysOptions}
                label="storeManagement.basicStores.manageStoreInfo.clubInfo.holidays"
                selected={getBasicStoreDetails?.holidays}
                {...methods.register('holidays')}
              />
              <Select
                options={closingDateOptions}
                label="storeManagement.basicStores.manageStoreInfo.clubInfo.closingDate"
                selected={getBasicStoreDetails?.closingDate}
                {...methods.register('closingDate')}
              />

              <MultiSelect
                name="otherHolidays"
                placeholder={t('common.form.placeholder.select')}
                label="storeManagement.basicStores.manageStoreInfo.clubInfo.otherHolidays"
                options={otherHolidaysOptions}
                className="flex"
                emptyIndicator={<p className="text-center text-sm">No results found</p>}
              />
            </div>
          </div>

          <SNS />
        </div>

        <div className="flex flex-row-reverse w-full gap-4 text-sm mt-6">
          <Can rule={{ allOf: [PERMS.club.basic.update] }}>
            <Button type="submit" className="w-[110px]">
              <Translation id="common.page.button.save" />
            </Button>
          </Can>
          <Link to="/store-information/basic-stores">
            <Button variant="outline" className="w-[122px] bg-transparent">
              <Translation id="common.page.button.cancel" />
            </Button>
          </Link>
        </div>
      </fieldset>
    </Form>
  );
};
